import { t } from "i18next";
import DBModal from "../../../../component/global/DBModal";
import DBInput from "../../../../component/global/DBInput";
import DBModalTitle from "../../../../component/global/DbModalTitle";
import DBFieldBlock from "../../../../component/global/DBFieldBlock";
import TreeSearchSelect from "./../../../../component/global/TreeSearchSelect/index";
import DBSelect from "../../../../component/global/DBSelect";
import DBButton from "../../../../component/global/DBButton";
import DBTextArea from "../../../../component/global/DBTextArea";
import { useContext, useEffect, useState } from "react";
import { useRequest } from "../../../../hooks/helperData/useRequest";
import {
    SETTING_ITEMS,
    SETTING_ITEMS_LIST,
    SETTING_ITEMS_TYPE_FAETURES_LIST,
} from "../../../../helperData/ApiAddress";
import DBSpinner from "../../../../component/global/DBSpinner";
import { ToastContext } from "../../../../component/global/ToastContext";
import { toBase64 } from "../../../../feedback/helperFunctions";
import DBMap from "../../../../component/global/DBMap";
import { getLanguageCaption } from "../../../../feedback/helperFunctions";
import LanguageTable from "./LanguageTable";
import FeaturesList from "./FeaturesList";
import DBUpload from "../../../../component/global/DBUpload";
import IncentiveQuickSetup, {
    createQuickIncentive,
    defaultQuickSetup,
} from "../../../../features/incentives/IncentiveQuickSetup";

export default function ItemsModal({
    showModal,
    onHide,
    onSuccess,
    editMode,
    itemType,
}) {
    const toast = useContext(ToastContext);
    const [quickSetup, setQuickSetup] = useState(() =>
        defaultQuickSetup("item")
    );
    const [partialTarget, setPartialTarget] = useState(null);
    const [quickSaving, setQuickSaving] = useState(false);

    const [form, setForm] = useState({
        name: "",
        photo: undefined,
        parent: undefined,
        description: "",
        features: [],
        translates: [],
        geo_point: {
            latitude: 35.685,
            longitude: 51.391,
        },
    });

    const [formErr, setFormErr] = useState({
        name: "",
    });

    const [sendItem, sendItemLoader] = useRequest(
        SETTING_ITEMS + (editMode ? `${editMode}/` : ""),
        editMode ? "PUT" : "POST"
    );

    const [getFeatures, getFeaturesLoader] = useRequest(
        SETTING_ITEMS_TYPE_FAETURES_LIST,
        "GET"
    );

    const [getItems, getItemsLoader] = useRequest(SETTING_ITEMS_LIST, "GET");
    const [getItemFields, getItemFieldsLoader] = useRequest(
        SETTING_ITEMS + editMode + "/",
        "GET",
        undefined,
        undefined,
        {
            "accept-language": "*",
        }
    );

    const [features, setFeatures] = useState([]);
    const [items, setItems] = useState([]);

    useEffect(() => {
        if (showModal) {
            setQuickSetup(defaultQuickSetup("item"));
            setPartialTarget(null);
            getFeatures({ item_type: itemType?.id }).then((r) => {
                setFeatures(r);
            });
            getItems({ item_type: itemType?.id }).then((r) => {
                setItems(r);
            });
            if (editMode) {
                getItemFields({ item_type: itemType?.id }).then((r) => {
                    const parent =
                        r.parent && typeof r.parent === "object"
                            ? r.parent
                            : r.parent
                              ? { id: r.parent }
                              : undefined;
                    setForm({
                        name: r.name || "",
                        photo: r.has_own_photo ? r.photo : undefined,
                        parent,
                        description: r.description || "",
                        features: (Array.isArray(r.features) ? r.features : [])
                            .filter((feature) => !feature.is_fixed)
                            .map((feature) => ({
                                value: feature.value,
                                feature:
                                    feature.feature &&
                                    typeof feature.feature === "object"
                                        ? feature.feature.id
                                        : feature.feature,
                            })),
                        translates: Array.isArray(r.translates)
                            ? r.translates
                            : [],
                        geo_point: r.geo_point || {
                            latitude: 35.685,
                            longitude: 51.391,
                        },
                        zoom: r.zoom || 11,
                    });
                });
            } else {
                setForm({
                    name: "",
                    photo: undefined,
                    parent: undefined,
                    description: "",
                    features: [],
                    translates: [],
                    geo_point: {
                        latitude: 35.685,
                        longitude: 51.391,
                    },
                    zoom: 11,
                });
            }

            setFormErr({
                name: "",
            });
        }
    }, [showModal]);

    const addEditItem = () => {
        let formErrTemp = {};

        if (form.name.trim() === "") {
            formErrTemp = { ...formErrTemp, name: t("item.name.required") };
        }

        setFormErr(formErrTemp);

        if (Object.keys(formErrTemp).length > 0) return false;

        sendItem({
            ...form,
            item_type: itemType?.id,
            parent: form.parent?.id || null,
            photo: form.photo?.indexOf("http") < 0 ? form.photo : undefined,
        })
            .then(async (saved) => {
                const target = {
                    id: saved?.id || editMode,
                    name: saved?.name || form.name,
                    type: "item",
                };
                if (quickSetup.enabled) {
                    setQuickSaving(true);
                    try {
                        await createQuickIncentive(quickSetup, target);
                    } catch {
                        setPartialTarget(target);
                        toast(t("incentives.quick.partialTitle"), "danger");
                        return;
                    } finally {
                        setQuickSaving(false);
                    }
                }
                onHide?.();
                onSuccess?.(saved);
                toast(t("item.save.success"), "success");
            })
            .catch((e) => {
                toast(t("item.save.error"), "danger");
            });
    };

    const retryQuickSetup = () => {
        setQuickSaving(true);
        createQuickIncentive(quickSetup, partialTarget)
            .then(() => {
                setPartialTarget(null);
                onHide?.();
                onSuccess?.(partialTarget);
                toast(t("item.save.success"), "success");
            })
            .catch(() => toast(t("incentives.error.publish"), "danger"))
            .finally(() => setQuickSaving(false));
    };

    const [language, setLanguage] = useState({
        name: "",
        description: "",
        language: "en",
    });
    const [languageEditMode, setLanguageEditMode] = useState(false);

    const addLanguageHandle = () => {
        if (languageEditMode !== false) {
            setForm({
                ...form,
                translates: form.translates.map((item, index) =>
                    index === languageEditMode ? language : item
                ),
            });
            setLanguageEditMode(false);
            setLanguage({
                name: "",
                description: "",
                language: "en",
            });
        } else {
            if (
                !form.translates.some(
                    (item) => item.language === language.language
                )
            ) {
                setForm({
                    ...form,
                    translates: [...form.translates, language],
                });
                setLanguage({
                    name: "",
                    description: "",
                    language: "en",
                });
            }
        }
    };

    const deleteLanguageHandle = (index) => {
        setForm({
            ...form,
            translates: form.translates.filter((_, ind) => ind !== index),
        });
    };

    const editLanguageHandle = (index) => {
        setLanguage(form.translates.find((_, ind) => ind === index));
        setLanguageEditMode(index);
    };

    return (
        <DBModal
            permanent
            show={showModal}
            minWidth={560}
            onHide={onHide}
            className="items-modal"
        >
            <DBModal.Header
                closeBtn={onHide}
                title={
                    <div>
                        {t("create.item")}
                        <div className="header-subcat">
                            {t("criteria.modal.subheader")} {itemType?.name}
                        </div>
                    </div>
                }
            />

            <DBModal.Content>
                {getFeaturesLoader || getItemsLoader || getItemFieldsLoader ? (
                    <DBSpinner tip={t("loading")} />
                ) : (
                    <>
                        <DBModalTitle>
                            {t("item.type.main.fields")}
                        </DBModalTitle>
                        <div className="grid">
                            <DBFieldBlock
                                title={t("items.name")}
                                error={formErr.name}
                                required
                            >
                                <DBInput
                                    onClick={() =>
                                        setFormErr({ ...formErr, name: "" })
                                    }
                                    status={formErr.name && "error"}
                                    type="text"
                                    size="large"
                                    value={form.name}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            name: e.target.value,
                                        })
                                    }
                                />
                            </DBFieldBlock>

                            <DBFieldBlock title={t("items.parent")}>
                                <TreeSearchSelect
                                    data={items}
                                    size="large"
                                    value={form.parent?.id}
                                    onChange={(e, p) => {
                                        setForm({
                                            ...form,
                                            parent: { id: e, name: p[0] },
                                        });
                                    }}
                                />
                            </DBFieldBlock>
                            <DBFieldBlock
                                title={t("items.description")}
                                span={2}
                            >
                                <DBTextArea
                                    rows={4}
                                    type="text"
                                    size="large"
                                    value={form.description}
                                    onChange={(e) =>
                                        setForm({
                                            ...form,
                                            description: e.target.value,
                                        })
                                    }
                                />
                            </DBFieldBlock>
                            <DBFieldBlock title="" span={2}>
                                <DBUpload
                                    accept=".jpg,.jpeg,.png"
                                    caption={
                                        form.photo
                                            ? t("items.delete.picture")
                                            : t("items.select.picture")
                                    }
                                    outlined
                                    type={form.photo ? "secondary" : "primary"}
                                    onChange={async (e) =>
                                        setForm({
                                            ...form,
                                            photo: await toBase64(
                                                e.target.files[0]
                                            ),
                                        })
                                    }
                                    onClick={(e) => {
                                        if (form.photo) {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            setForm({ ...form, photo: "" });
                                        }
                                    }}
                                    maxSize={1024 * 1024}
                                />
                            </DBFieldBlock>
                        </div>
                        <DBModalTitle>{t("items.features")}</DBModalTitle>
                        <div className="grid">
                            <FeaturesList
                                features={features}
                                value={form.features}
                                onChange={(e) =>
                                    setForm({ ...form, features: e })
                                }
                            />
                        </div>
                        <DBModalTitle>{t("items.language")}</DBModalTitle>
                        <div className="grid">
                            <DBFieldBlock title={t("items.name")}>
                                <DBInput
                                    type="text"
                                    size="large"
                                    value={language.name}
                                    onChange={(e) =>
                                        setLanguage({
                                            ...language,
                                            name: e.target.value,
                                        })
                                    }
                                />
                            </DBFieldBlock>

                            <DBFieldBlock title={t("items.parent")}>
                                <DBSelect
                                    value={language.language}
                                    onChange={(e) =>
                                        setLanguage({
                                            ...language,
                                            language: e,
                                        })
                                    }
                                    options={[
                                        {
                                            value: "en",
                                            label: getLanguageCaption("en"),
                                        },
                                        {
                                            value: "ar",
                                            label: getLanguageCaption("ar"),
                                        },
                                    ]}
                                    size="large"
                                />
                            </DBFieldBlock>
                            <DBFieldBlock
                                title={t("items.description")}
                                span={2}
                            >
                                <DBTextArea
                                    rows={4}
                                    type="text"
                                    size="large"
                                    value={language.description}
                                    onChange={(e) =>
                                        setLanguage({
                                            ...language,
                                            description: e.target.value,
                                        })
                                    }
                                />
                            </DBFieldBlock>
                            <DBFieldBlock title="" span={2}>
                                <DBButton
                                    caption={
                                        languageEditMode === false
                                            ? t("items.add.language")
                                            : t("items.edit.language")
                                    }
                                    disabled={
                                        !language.name || !language.description
                                    }
                                    className="fullwidth-button"
                                    outlined
                                    onClick={addLanguageHandle}
                                />
                            </DBFieldBlock>
                        </div>
                        {form.translates.length > 0 && (
                            <div className="language-table">
                                <LanguageTable
                                    data={form.translates.map((item) => ({
                                        ...item,
                                        language:
                                            item.language === "en"
                                                ? t("item.type.language.en")
                                                : t("item.type.language.ar"),
                                    }))}
                                    onEdit={editLanguageHandle}
                                    onDelete={deleteLanguageHandle}
                                />
                            </div>
                        )}
                        <DBModalTitle>{t("items.location")}</DBModalTitle>

                        <DBMap
                            value={{ ...form.geo_point, zoom: form.zoom }}
                            onChange={(e) => {
                                setForm({
                                    ...form,
                                    geo_point: e,
                                    zoom: e.zoom,
                                });
                            }}
                        />
                        <IncentiveQuickSetup
                            value={quickSetup}
                            onChange={setQuickSetup}
                            targetType="item"
                            partialFailure={Boolean(partialTarget)}
                            retrying={quickSaving}
                            onRetry={retryQuickSetup}
                        />
                    </>
                )}
            </DBModal.Content>
            <DBModal.Footer>
                <DBButton
                    caption={t("save")}
                    onClick={addEditItem}
                    loading={sendItemLoader || quickSaving}
                />
                <DBButton
                    caption={t("cancel")}
                    type="secondary"
                    onClick={onHide}
                />
            </DBModal.Footer>
        </DBModal>
    );
}
